import React, { Component } from 'react';
import compose from 'lodash/flowRight';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { graphql, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import { withSnackbar } from 'notistack';
import omit from 'lodash/omit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { Collapse, Tooltip } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Visibility from '@material-ui/icons/Visibility';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';

import { memberClient } from 'src/config/Apollo';
import MHSelect from 'src/components/MHSelect';

import CoachSettingsRow from '../components/CoachSettingsRow';
import LocationUpdate from '../components/Dialogs/LocationUpdate';
import MHButton from '../components/MHButton';
import MHInput from '../components/MHInput';
import PageContainer from '../components/PageContainer';
import PasswordUpdateDialog from '../components/Dialogs/PasswordUpdate';
import http from '../services/Http';
import { colors } from '../themes/colors';
import { store } from '../config/Store';
import { textInputStyle } from '../themes/input';

const styles = (theme) => ({
  ...textInputStyle(theme),
  settingsPage: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflowY: 'auto',
  },
  header: {
    height: 245,
    minHeight: 245,
    backgroundImage: 'radial-gradient(circle at 48% 0, #69d8ec, #00b6ca)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      height: 145,
      minHeight: 145,
    },
  },
  headerText: {
    color: 'white',
  },
  headerFullName: {
    fontFamily: 'Avenir-Book',
    fontSize: 30,
  },
  body: {
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.gray9,
    flexGrow: 1,
    minHeight: '100%',
  },
  bodyTitle: {
    color: colors.blue2,
    fontSize: 30,
    marginBottom: 30,
  },
  cardContainer: {
    flexGrow: 1,
  },
  settingsCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: 24,
    minHeight: '100%',
  },
  accountDetailTitleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cancelSubmitRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  cardTitle: {
    color: colors.black2,
    fontFamily: 'Avenir-Black',
    marginBottom: 25,
  },
  editDetails: {
    display: 'flex',
    flexDirection: 'row',
    cursor: 'pointer',
    color: colors.blue1,
  },
  editIcon: {
    width: 13,
    height: 13,
    marginRight: 2,
    marginTop: 2,
  },
  warningIcon: {
    maxHeight: 20,
    marginTop: 2,
    marginRight: 20,
  },
  accountSaveButton: {
    marginTop: 20,
    width: '100%',
  },
  updatePasswordButton: {
    color: theme.palette.primary.main,
  },
  submitButtonSize: {
    width: '100%',
  },
  cancelButton: {
    backgroundColor: colors.white1,
    color: colors.blue6,
    width: '100%',
    border: `1px solid ${colors.blue6}`,
    '&:hover': {
      backgroundColor: colors.blue7,
      color: colors.blue6,
    },
  },
  remainingAvailability: {
    background: '#E9EEF4',
    borderRadius: 4,
    padding: 20,
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 153,
  },
  availabilityWarningSection: {
    borderRadius: 4,
    padding: 24,
    margin: '-24px -24px 24px -24px',
    display: 'flex',
    flexDirection: 'row',
    minHeight: '150px',
  },
  availabilityTitle: {
    fontWeight: 800,
    fontSize: 14,
    color: '#4D6580',
  },
  availabilityNumber: {
    fontWeight: 900,
    fontSize: 28,
    color: colors.blue6,
    marginTop: 10,
  },
  clientCapacitySelect: {
    background: colors.white1,
    border: `2px solid ${colors.blue7}`,
    textAlign: 'left',
    marginTop: 10,
    minWidth: 320,
    fontSize: 14,
    color: colors.black2,
  },
});

const maxNoOfClientMatches = 30;

class Settings extends Component {
  state = {
    // form fields
    fullName: '',
    email: '',
    introduction: '',
    credentials: '',
    selectedGoalCategoryIds: [],
    languagePreferences: [],
    country: '',
    zipCode: '',
    city: '',
    state: '',
    newPassword: '',
    confirmNewPassword: '',

    // form field validity
    fullNameIsValid: true,
    emailIsValid: true,

    // normal state
    accountDetailsLoading: false,
    profileDetailsLoading: false,
    passwordDialogOpen: false,
    locationDialogOpen: false,
    currentlyEditing: false,
    showClientCapacityWarningMessage: false,
    isCoachProvider: false,
    hasClientCapacityError: false,
  };

  componentDidMount() {
    const state = this.getInitialValues();
    this.setState(state);
  }

  getInitialValues = () => {
    const { currentUser, currentUserLatestEmail, currentUserSelectedGoalCategoryIds } = this.props;

    return {
      fullName: `${currentUser.firstName} ${currentUser.lastName}`,
      email: currentUserLatestEmail,
      introduction: currentUser.introduction,
      credentials: currentUser.credentials,
      selectedGoalCategoryIds: currentUserSelectedGoalCategoryIds,
      languagePreferences: currentUser.languagePreferences || [],
      country: currentUser.country || '',
      zipCode: currentUser.zipCode || '',
      city: currentUser.city || '',
      state: currentUser.state || '',
      currentClientCapacity: currentUser.newClientCapacity ?? 0, // it's value from API
      clientCapacityRange: this.setClientCapacityRange(), // value to handle dropdown (if coach)
      newClientCapacity: currentUser.newClientCapacity ?? 0, // value to handle input (if therapist) & dropdown value
      showClientCapacityWarningMessage: this.showClientCapacityWarningMessage(),
      isCoachProvider: currentUser.isCoach,
      providerSchedulingType: currentUser.providerSchedulingType,
      hasClientCapacityError: false,
    };
  };

  setClientCapacityRange = () => {
    const { currentUser } = this.props;
    if (currentUser.newClientCapacity === null || currentUser.newClientCapacity === 0) {
      return '0';
    }
    if (currentUser.newClientCapacity > 0 && currentUser.newClientCapacity <= 5) {
      return '5';
    }
    if (currentUser.newClientCapacity > 5 && currentUser.newClientCapacity <= 10) {
      return '10';
    }
    if (currentUser.newClientCapacity > 10 && currentUser.newClientCapacity <= 15) {
      return '15';
    }
    if (currentUser.newClientCapacity > 15 && currentUser.newClientCapacity <= 20) {
      return '20';
    }
    if (currentUser.newClientCapacity > 20 && currentUser.newClientCapacity <= 25) {
      return '25';
    }
    if (currentUser.newClientCapacity > 25 && currentUser.newClientCapacity <= 30) {
      return '30';
    }
  };

  handleNewClientCapacity = (event) => {
    const intValue = parseInt(event.target.value, 10) || 0;

    if (intValue > maxNoOfClientMatches) {
      this.setState({ hasClientCapacityError: true });
    } else if (this.state.hasClientCapacityError) {
      this.setState({ hasClientCapacityError: false });
    }

    this.setState({
      newClientCapacity: intValue,
      clientCapacityRange: event.target.value,
    });
  };

  handleChange = (propertyToChange) => {
    return (event) => {
      // set the property
      this.setState({
        [propertyToChange]: event.target.value,
      });

      // set if the property is valid (if method exists)
      return this[`${propertyToChange}Valid`]?.(event.target.value);
    };
  };

  fullNameValid = (name = '') => {
    // full name should have at least two words in them
    const trimmedName = name.trim();
    const fullNameIsValid = trimmedName.length > 0 && trimmedName.split(' ').length > 1;
    this.setState({ fullNameIsValid });
  };

  emailValid = (email = '') => {
    const emailIsValid = email.length > 0;
    this.setState({ emailIsValid });
  };

  saveAccountDetails = async () => {
    const { currentUser } = this.props;
    const { fullName, email } = this.state;
    const trimmedName = fullName.trim().split(' ');
    const firstName = trimmedName.shift();
    const lastName = trimmedName.join(' ');

    try {
      this.setState({ accountDetailsLoading: true });
      await http.formDataPATCH(`accounts/${currentUser.id}/`, { firstName, lastName, email });
      this.props.fetchCurrentUser();
      this.props.enqueueSnackbar('Save success!', { variant: 'success' });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setState({ accountDetailsLoading: false });
    }
  };

  getRequiredVariablesToSaveData = () => {
    const { fullName, email, newPassword } = this.state;
    const trimmedName = fullName.trim().split(' ');
    const firstName = trimmedName.shift();
    const lastName = trimmedName.join(' ');
    return {
      email: email,
      password: newPassword,
      firstName,
      lastName,
    };
  };

  isUpdatingPassword = () => {
    const { newPassword, confirmNewPassword } = this.state;
    return !!newPassword.length && newPassword === confirmNewPassword;
  };

  cleanPasswordFields = () => {
    this.setState({ newPassword: '', confirmNewPassword: '' });
  };

  saveAccountAndPassword = async () => {
    const { currentUser } = this.props;
    const variables = this.getRequiredVariablesToSaveData();
    const requestParams = this.isUpdatingPassword() ? variables : omit(variables, 'password');
    let backendErrorFree = true;

    if (variables.password && !this.isUpdatingPassword()) {
      this.props.enqueueSnackbar('Passwords must match', { variant: 'error' });
      return;
    }
    try {
      await http.formDataPATCH(`accounts/${currentUser.id}/`, requestParams);
      this.props.fetchCurrentUser();
      this.props.enqueueSnackbar('Save success!', { variant: 'success' });
    } catch (error) {
      backendErrorFree = false;
      this.handleError(error);
    } finally {
      if (backendErrorFree) {
        this.setState({ currentlyEditing: false });
        this.cleanPasswordFields();
      }
    }
  };

  saveNewCoachClientCapacity = async () => {
    const { newClientCapacity } = this.state;

    try {
      await memberClient.mutate({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        mutation: saveNewClientCapacity,
        variables: { updatedNewClientCapacity: newClientCapacity },
      });
      this.setState({ currentClientCapacity: newClientCapacity });
      this.setState({ showClientCapacityWarningMessage: false });
      this.showClientCapacityWarningMessage();
      this.props.fetchCurrentUser();
      this.props.enqueueSnackbar('Save success!', { variant: 'success' });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setState({ currentlyEditingRosterNumber: false });
    }
  };

  // TODO: abstract this to global error handling
  handleError = (error) => {
    const errors = [];
    if (error.body) {
      Object.keys(error.body).forEach((key) => {
        if (typeof error.body[key] === 'string') {
          errors.push(error.body[key]);
        } else if (Array.isArray(error.body[key])) {
          errors.push(...error.body[key]);
        }
      });
    } else {
      errors.push('Something went wrong saving info.');
    }
    this.props.enqueueSnackbar(errors.join(' '), { variant: 'error' });
  };

  openPasswordDialog = () => {
    this.setState({ passwordDialogOpen: true });
  };

  closePasswordDialog = () => {
    this.setState({ passwordDialogOpen: false });
  };

  currentlyEditingTrue = () => {
    this.setState({ currentlyEditing: true });
  };

  currentlyEditingFalse = () => {
    this.setState({ currentlyEditing: false });
    this.cleanPasswordFields();
  };

  currentlyEditingRosterTrue = () => {
    this.setState({ currentlyEditingRosterNumber: true });
  };

  currentlyEditingRosterFalse = () => {
    this.setState({
      currentlyEditingRosterNumber: false,
      newClientCapacity: this.state.currentClientCapacity,
      hasClientCapacityError: false,
    });
  };

  handleClickShowNewPassword = async () => {
    this.setState({ showNewPassword: !this.state.showNewPassword });
  };

  handleClickShowConfirmPassword = async () => {
    this.setState({ showConfirmPassword: !this.state.showConfirmPassword });
  };

  showClientCapacityWarningMessage = () => {
    const { currentUser } = this.props;
    const rosterCountRunningLow = [0, 1, 2];
    return rosterCountRunningLow.includes(currentUser.newClientCapacity);
  };

  clientCapacityIsZero = () => {
    const { currentClientCapacity } = this.state;
    return currentClientCapacity === 0;
  };

  lowClientCapacityTextMessage = () => {
    return this.clientCapacityIsZero() ? 'Zero' : 'Running Low';
  };

  resetSessions = async () => {
    try {
      this.setState({ accountDetailsLoading: true });
      await http.formDataPOST('accounts/reset/?target=session', {});
      this.props.fetchCurrentUser();
      this.props.enqueueSnackbar('Sessions have been reset!', { variant: 'success' });
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setState({ accountDetailsLoading: false });
    }
  };

  locationUpdateButtonLabel = (city, state, country) => {
    const locationSpecifiers = [];
    if (city) {
      locationSpecifiers.push(city);
    }
    if (state) {
      locationSpecifiers.push(state);
    }
    if (country) {
      locationSpecifiers.push(country);
    }
    if (locationSpecifiers.length > 0) {
      return locationSpecifiers.join(', ');
    } else {
      return 'Location not set';
    }
  };

  getStylesMultiSelect = (categoryId) => {
    return {
      fontWeight:
        this.state.selectedGoalCategoryIds.indexOf(categoryId) === -1
          ? this.props.theme.typography.fontWeightRegular // not selected
          : this.props.theme.typography.fontWeightMedium, // selected
    };
  };

  get accountDetailsDisabled() {
    const { currentUser, currentUserLatestEmail } = this.props;
    const { fullName, email, fullNameIsValid, emailIsValid, accountDetailsLoading } = this.state;

    const accountDetailsPristine =
      fullName === `${currentUser.firstName} ${currentUser.lastName}` &&
      email === currentUserLatestEmail;
    const accountDetailsValid = fullNameIsValid && emailIsValid;

    // If pristine or if invalid or if loading, then always disabled
    return accountDetailsPristine || !accountDetailsValid || accountDetailsLoading;
  }

  get accountAndPasswordDetailsDisabled() {
    const { currentUser, currentUserLatestEmail } = this.props;
    const { fullName, email, fullNameIsValid, emailIsValid, accountDetailsLoading } = this.state;
    const { newPassword, confirmNewPassword } = this.state;

    const accountDetailsPristine =
      fullName === `${currentUser.firstName} ${currentUser.lastName}` &&
      email === currentUserLatestEmail;
    const accountDetailsValid = fullNameIsValid && emailIsValid;

    // If fullName and email remains the same but updates password
    if (newPassword.length && accountDetailsPristine) {
      return !newPassword.length || !confirmNewPassword.length;
    }

    // If fullName and email changes and also updates password
    if (newPassword.length && !accountDetailsPristine) {
      return (
        !newPassword.length ||
        !confirmNewPassword.length ||
        !accountDetailsValid ||
        accountDetailsLoading
      );
    }

    // If pristine or if invalid or if loading, then always disabled
    return accountDetailsPristine || !accountDetailsValid || accountDetailsLoading;
  }

  render() {
    const {
      currentUser,
      currentUserLatestEmail,
      classes,
      getGoalCategoriesQuery,
      canViewDebugInfo,
    } = this.props;

    const {
      fullName,
      email,
      languagePreferences,
      introduction,
      credentials,
      selectedGoalCategoryIds,
      accountDetailsLoading,
      passwordDialogOpen,
      locationDialogOpen,
      currentlyEditing,
      currentlyEditingRosterNumber,
      newPassword,
      confirmNewPassword,
      showNewPassword,
      showConfirmPassword,
      currentClientCapacity,
      clientCapacityRange,
      newClientCapacity,
      showClientCapacityWarningMessage,
      isCoachProvider,
      providerSchedulingType,
      hasClientCapacityError,
    } = this.state;

    const goalCategories = (getGoalCategoriesQuery.goalCategories || []).filter(
      (goalCategory) => !goalCategory.parent && goalCategory.isInOnboarding,
    );

    const specializations = currentUser.isTherapist
      ? credentials
      : goalCategories
          .filter((category) => selectedGoalCategoryIds.includes(category.id))
          .map((category) => category.name)
          .join(', ');

    return (
      <PageContainer position="inherit">
        <div className={classes.settingsPage}>
          <div className={classes.header}>
            <Typography className={classNames([classes.headerText, classes.headerFullName])}>
              {currentUser.firstName} {currentUser.lastName}
            </Typography>
            <Typography variant="body1" className={classes.headerText}>
              {currentUserLatestEmail}
            </Typography>
          </div>
          <div className={classes.body}>
            <Typography variant="h6" className={classes.bodyTitle}>
              Account Settings
            </Typography>

            <Grid container spacing={3} className={classes.cardContainer}>
              {/* For Therapists: Account details*/}
              {!isCoachProvider && (
                <Grid item xs={12} md={4}>
                  <Paper className={classes.settingsCard}>
                    <div>
                      <Typography variant="subtitle1" className={classes.cardTitle}>
                        Your account details
                      </Typography>

                      <CoachSettingsRow title="Name">
                        <MHInput value={fullName} onChange={this.handleChange('fullName')} />
                      </CoachSettingsRow>
                    </div>

                    <MHButton
                      children="Save Updates"
                      onClick={this.saveAccountDetails}
                      disabled={this.accountDetailsDisabled}
                      loading={accountDetailsLoading}
                      extraClasses={[classes.accountSaveButton]}
                    />
                  </Paper>
                </Grid>
              )}

              {/* For Coaches: Account details and password update*/}
              {isCoachProvider && (
                <Grid item xs={12} md={4}>
                  <Paper className={classes.settingsCard}>
                    <div>
                      <div className={classes.accountDetailTitleRow}>
                        <Typography variant="subtitle1" className={classes.cardTitle}>
                          Your account details
                        </Typography>

                        {!currentlyEditing && (
                          <label
                            className={classes.editDetails}
                            onClick={this.currentlyEditingTrue}
                          >
                            <img
                              className={classes.editIcon}
                              src={require('../assets/images/pencil-edit.png')}
                              alt="resource-item"
                            />
                            <Typography>Edit</Typography>
                          </label>
                        )}
                      </div>

                      <CoachSettingsRow title="Name">
                        {!currentlyEditing && <Typography>{fullName}</Typography>}
                        {currentlyEditing && (
                          <MHInput
                            value={fullName}
                            onChange={this.handleChange('fullName')}
                            disabled={!currentlyEditing}
                          />
                        )}
                      </CoachSettingsRow>

                      {!currentlyEditing && (
                        <CoachSettingsRow title="Password">
                          {!currentlyEditing && <Typography> ****** </Typography>}
                          {currentlyEditing && (
                            <Button onClick={this.openPasswordDialog}>
                              <Typography
                                variant="body1"
                                className={classNames([
                                  classes.textFieldInput,
                                  classes.updatePasswordButton,
                                ])}
                              >
                                Update Password
                              </Typography>
                            </Button>
                          )}
                        </CoachSettingsRow>
                      )}

                      {currentlyEditing && (
                        <CoachSettingsRow title="New Password">
                          <TextField
                            margin="dense"
                            variant="outlined"
                            value={newPassword}
                            onChange={this.handleChange('newPassword')}
                            className={classes.textField}
                            InputProps={{
                              className: classes.textFieldInput,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="Toggle password visibility"
                                    onClick={this.handleClickShowNewPassword}
                                  >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            type={showNewPassword ? 'text' : 'password'}
                          />
                        </CoachSettingsRow>
                      )}

                      {currentlyEditing && (
                        <CoachSettingsRow title="Confirm New Password">
                          <TextField
                            margin="dense"
                            variant="outlined"
                            value={confirmNewPassword}
                            onChange={this.handleChange('confirmNewPassword')}
                            className={classes.textField}
                            InputProps={{
                              className: classes.textFieldInput,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="Toggle password visibility"
                                    onClick={this.handleClickShowConfirmPassword}
                                  >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            type={showConfirmPassword ? 'text' : 'password'}
                          />
                        </CoachSettingsRow>
                      )}
                    </div>

                    {currentlyEditing && (
                      <div className={classes.cancelSubmitRow}>
                        <MHButton
                          children="Cancel"
                          onClick={this.currentlyEditingFalse}
                          className={classes.cancelButton}
                          wrapperWidth="48%"
                        />
                        <MHButton
                          children="Save"
                          onClick={this.saveAccountAndPassword}
                          disabled={this.accountAndPasswordDetailsDisabled}
                          className={classes.submitButtonSize}
                          wrapperWidth="48%"
                        />
                      </div>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* For Therapists: Password update*/}
              {!isCoachProvider && (
                <Grid item xs={12} md={4}>
                  <Paper className={classes.settingsCard}>
                    <div>
                      <Typography variant="subtitle1" className={classes.cardTitle}>
                        Password
                      </Typography>
                      <Button onClick={this.openPasswordDialog}>
                        <Typography
                          variant="body1"
                          className={classNames([
                            classes.textFieldInput,
                            classes.updatePasswordButton,
                          ])}
                        >
                          Update Password
                        </Typography>
                      </Button>
                    </div>

                    <MHButton
                      children="Change Password"
                      onClick={this.saveAccountDetails}
                      disabled={this.accountDetailsDisabled}
                      loading={accountDetailsLoading}
                      extraClasses={[classes.accountSaveButton]}
                    />
                  </Paper>
                </Grid>
              )}

              {(isCoachProvider || providerSchedulingType === 'Calendar') && (
                <Grid item xs={12} md={4}>
                  <Paper className={classes.settingsCard}>
                    <div>
                      {showClientCapacityWarningMessage && (
                        <div
                          className={`${classes.availabilityWarningSection}
                          ${this.clientCapacityIsZero() ? 'bg-red-200' : 'bg-orange-200'}`}
                        >
                          <img
                            className={classes.warningIcon}
                            src={require('../assets/images/caution.png')}
                            alt="resource-item"
                          />
                          <div>
                            <Typography variant="subtitle1" className={classes.cardTitle}>
                              New Match Availability is {this.lowClientCapacityTextMessage()}
                            </Typography>

                            <Typography>
                              Heads up, your new match availability is{' '}
                              {this.lowClientCapacityTextMessage().toLowerCase()}. If you would like
                              to receive more new matches, we recommend increasing this number.
                            </Typography>
                          </div>
                        </div>
                      )}

                      <div className={classes.accountDetailTitleRow}>
                        <Typography variant="subtitle1" className={classes.cardTitle}>
                          Client Matching
                        </Typography>
                        {!currentlyEditingRosterNumber && (
                          <label
                            className={classes.editDetails}
                            onClick={this.currentlyEditingRosterTrue}
                          >
                            <img
                              className={classes.editIcon}
                              src={require('../assets/images/pencil-edit.png')}
                              alt="resource-item"
                            />
                            <Typography>Edit</Typography>
                          </label>
                        )}
                      </div>
                      <Typography>
                        This feature allows you to choose the number of new matches you are
                        currently open to receiving. It will auto-update as you receive new matches.
                        You can change this number at any time.
                      </Typography>
                      <div className={classes.remainingAvailability}>
                        <Typography className={classes.availabilityTitle}>
                          Remaining Availability
                        </Typography>

                        {!currentlyEditingRosterNumber && (
                          <Typography className={classes.availabilityNumber}>
                            {currentClientCapacity ?? 0}
                          </Typography>
                        )}

                        {currentlyEditingRosterNumber &&
                          (currentUser.isTherapist || currentUser.isPsychiatrist) && (
                            <>
                              <TextField
                                margin="dense"
                                variant="outlined"
                                value={newClientCapacity}
                                onChange={this.handleNewClientCapacity}
                                className={classes.textField}
                                InputProps={{
                                  className: classes.textFieldInput,
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                  inputProps: {
                                    min: 0,
                                    max: maxNoOfClientMatches,
                                  },
                                }}
                                type="number"
                              />

                              <Collapse in={hasClientCapacityError}>
                                <FormHelperText error>
                                  The maximum number of clients is {maxNoOfClientMatches}
                                </FormHelperText>
                              </Collapse>
                            </>
                          )}

                        {currentlyEditingRosterNumber &&
                          !currentUser.isTherapist &&
                          !currentUser.isPsychiatrist && (
                            <MHSelect
                              extraClass={classes.clientCapacitySelect}
                              value={clientCapacityRange}
                              onChange={this.handleNewClientCapacity}
                            >
                              <MenuItem value="0">None</MenuItem>
                              <MenuItem value="5">Up to 5 clients</MenuItem>
                              <MenuItem value="10">Up to 10 clients</MenuItem>
                              <MenuItem value="15">Up to 15 clients</MenuItem>
                              <MenuItem value="20">Up to 20 clients</MenuItem>
                              <MenuItem value="25">Up to 25 clients</MenuItem>
                              <MenuItem value="30">Up to 30 clients</MenuItem>
                            </MHSelect>
                          )}
                      </div>
                    </div>

                    {currentlyEditingRosterNumber && (
                      <div className={classes.cancelSubmitRow}>
                        <MHButton
                          children="Cancel"
                          onClick={this.currentlyEditingRosterFalse}
                          className={classes.cancelButton}
                          wrapperWidth="48%"
                        />
                        <MHButton
                          children="Save"
                          onClick={this.saveNewCoachClientCapacity}
                          className={classes.submitButtonSize}
                          wrapperWidth="48%"
                          disabled={hasClientCapacityError}
                        />
                      </div>
                    )}
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <Paper className={classes.settingsCard}>
                  <div>
                    <Typography variant="subtitle1" className={classes.cardTitle}>
                      Your profile
                    </Typography>

                    <CoachSettingsRow title="Bio">
                      <Typography>{introduction}</Typography>
                    </CoachSettingsRow>

                    <CoachSettingsRow title="Email Address">
                      <Typography>{email}</Typography>
                    </CoachSettingsRow>

                    <CoachSettingsRow title="Specializations">
                      <Typography>{specializations}</Typography>
                    </CoachSettingsRow>

                    <CoachSettingsRow title="Language Preference">
                      <Typography>{languagePreferences.join(', ')}</Typography>
                    </CoachSettingsRow>

                    <CoachSettingsRow title="Location">
                      <Typography>
                        {this.locationUpdateButtonLabel(
                          currentUser.city || '',
                          currentUser.state || '',
                          currentUser.country || '',
                        )}
                      </Typography>
                    </CoachSettingsRow>
                    <CoachSettingsRow>
                      <Typography>
                        If you want to update your profile, send an email to{' '}
                        <a href="mailto:provider@joinmodernhealth.com">
                          provider@joinmodernhealth.com
                        </a>
                        .
                      </Typography>
                    </CoachSettingsRow>
                  </div>
                  <div>
                    {/*The view-debug-info feature flag is only available in staging and production.
                     process.env.NODE_ENV !== 'production' only gives users access to this button in develop.*/}
                    {(canViewDebugInfo || process.env.NODE_ENV !== 'production') && (
                      <Tooltip
                        title="Resets sessions for the provider and all its members"
                        placement="left"
                        arrow
                      >
                        <div>
                          <MHButton color="secondary" onClick={this.resetSessions}>
                            Reset Sessions
                          </MHButton>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </div>

          {passwordDialogOpen && (
            <PasswordUpdateDialog
              closeDialog={this.closePasswordDialog}
              handleError={this.handleError}
              currentUser={currentUser}
            />
          )}
          {locationDialogOpen && (
            <LocationUpdate closeDialog={this.closeLocationDialog} handleError={this.handleError} />
          )}
        </div>
      </PageContainer>
    );
  }
}

const getGoalCategories = gql`
  query getGoalCategories {
    goalCategories {
      id
      name
      isInOnboarding
      parent {
        id
      }
    }
  }
`;

const saveNewClientCapacity = gql`
  mutation saveNewClientCapacity($updatedNewClientCapacity: Int!) {
    saveNewClientCapacity(updatedNewClientCapacity: $updatedNewClientCapacity) {
      newClientCapacity
    }
  }
`;

// Workaround to be able to use `useFlags` hook without rewriting the whole component into functional one
const SettingsFFWrapper = (props) => {
  const flags = useFlags();

  return <Settings {...props} canViewDebugInfo={flags.viewDebugInfo} />;
};

const mapState = (state) => ({
  currentUser: state.user.currentUser,
  currentUserSelectedGoalCategoryIds: store.select.user.selectedGoalCategoryIds(state),
  currentUserLatestEmail: store.select.user.latestEmail(state),
});

const mapDispatch = (store) => ({
  fetchCurrentUser: () => store.user.fetchCurrentUser(),
});

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(mapState, mapDispatch),
  withSnackbar,
  withApollo,
  graphql(getGoalCategories, {
    name: 'getGoalCategoriesQuery',
    options: () => ({
      fetchPolicy: 'network-only',
    }),
  }),
  graphql(saveNewClientCapacity, { name: 'saveNewClientCapacityMutation' }),
)(SettingsFFWrapper);
